import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, apiDownload } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ESTATUS_REPORTE_LABEL,
  type EstatusReporte,
  type OperativoDetalleRow,
  type Paginated,
  type ReporteOperativoRow,
} from '@/lib/types';

const ALL = '__all__';
const PER_PAGE_OPCIONES = [100, 250, 500, 1000];

const ESTATUS_CLASS: Record<EstatusReporte, string> = {
  abierto: 'bg-ink-100 text-ink-700',
  en_transito: 'bg-blue-100 text-blue-700',
  pre_arribo: 'bg-amber-100 text-amber-700',
  arribado: 'bg-green-100 text-green-700',
  vencido: 'bg-red-100 text-red-700',
  cerrado: 'bg-ink-200 text-ink-600',
  sin_fecha: 'bg-ink-100 text-ink-500',
};

function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

interface Filtros {
  posicion?: string;
  desde: string;
  hasta: string;
  agenteId?: number;
  cliente: string;
  page: number;
  perPage: number;
}
const DEFAULT: Filtros = { desde: '', hasta: '', cliente: '', posicion: '', page: 1, perPage: 100 };

export function ReporteOperativosPage() {
  const [f, setF] = useState<Filtros>(DEFAULT);
  const [exporting, setExporting] = useState(false);
  const [detalle, setDetalle] = useState<ReporteOperativoRow | null>(null);

  const params = {
    desde: f.desde || undefined,
    hasta: f.hasta || undefined,
    agenteId: f.agenteId,
    cliente: f.cliente || undefined,
    posicion: f.posicion || undefined,
    page: f.page,
    perPage: f.perPage,
  };

  const q = useQuery({
    queryKey: ['reporte-operativos', f],
    queryFn: () =>
      api<Paginated<ReporteOperativoRow>>('/reports/operativos', { params }),
  });
  const agentesQ = useQuery({
    queryKey: ['reporte-agentes'],
    queryFn: () => api<{ items: { id: number; nombre: string }[] }>('/team/agentes'),
  });

  async function onExport() {
    setExporting(true);
    try {
      await apiDownload('/reports/operativos/export', 'Reporte Operativos.xlsx', {
        desde: f.desde || undefined,
        hasta: f.hasta || undefined,
        agenteId: f.agenteId,
        cliente: f.cliente || undefined,
        posicion: f.posicion || undefined,
      });
    } finally {
      setExporting(false);
    }
  }

  const data = q.data;
  const rows = data?.items ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 w-[95%] max-w-none mx-auto">
      <style>{`
        .report-scroll { overflow: scroll; max-height: 70vh; }
        .report-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .report-scroll::-webkit-scrollbar-track { background: var(--color-surface-2); }
        .report-scroll::-webkit-scrollbar-thumb { background: var(--color-ink-300); border-radius: 7px; border: 3px solid var(--color-surface-2); }
        .report-scroll { scrollbar-width: auto; }
      `}</style>

      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Reportes · Tareas</h1>
          <p className="text-sm text-ink-400">
            {data ? `${data.pagination.total} shipments` : 'Cargando…'}
          </p>
        </div>
        <Button onClick={onExport} disabled={exporting || !rows.length} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? 'Generando…' : 'Exportar a Excel'}
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-2 p-2 rounded-lg bg-surface-2">
        <div>
          <label className="block text-[11px] text-ink-400 mb-0.5">Desde</label>
          <Input
            type="date"
            value={f.desde}
            onChange={(e) => setF({ ...f, desde: e.target.value, page: 1 })}
            className="w-36"
          />
        </div>
        <div>
          <label className="block text-[11px] text-ink-400 mb-0.5">Hasta</label>
          <Input
            type="date"
            value={f.hasta}
            onChange={(e) => setF({ ...f, hasta: e.target.value, page: 1 })}
            className="w-36"
          />
        </div>
        <div>
          <label className="block text-[11px] text-ink-400 mb-0.5">Operativo</label>
          <Select
            value={f.agenteId ? String(f.agenteId) : ALL}
            onValueChange={(v) =>
              setF({ ...f, agenteId: v === ALL ? undefined : Number(v), page: 1 })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Operativo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos</SelectItem>
              {(agentesQ.data?.items ?? []).map((a) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[11px] text-ink-400 mb-0.5">Cliente</label>
          <Input
            placeholder="Buscar cliente…"
            value={f.cliente}
            onChange={(e) => setF({ ...f, cliente: e.target.value, page: 1 })}
            className="w-52"
          />
        </div>
        <div>
          <label className="block text-[11px] text-ink-400 mb-0.5">Booking</label>
          <Input
            placeholder="Buscar posición (BO)…"
            value={f.posicion ?? ''}
            onChange={(e) => setF({ ...f, posicion: e.target.value, page: 1 })}
            className="w-44"
          />
        </div>
        {(f.desde || f.hasta || f.agenteId || f.cliente || f.posicion) && (
          <Button variant="ghost" size="sm" onClick={() => setF(DEFAULT)}>
            Limpiar
          </Button>
        )}
      </div>

      {/* Tabla */}
      <div className="report-scroll rounded-lg border border-ink-200 bg-surface-1">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              {[
                'Operativo',
                'Cliente',
                'Posición (BO)',
                'Status Embarque',
                'Fecha shipment',
                'Completadas',
                'Pendientes',
                'Vencidas',
                'Detalles',
              ].map((h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold border border-ink-100 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-ink-400">
                  Cargando…
                </td>
              </tr>
            )}
            {!q.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-ink-400">
                  Sin resultados con estos filtros.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.posicion}-${i}`} className="hover:bg-surface-2">
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap font-medium">{r.operativo || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{r.cliente || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{r.posicion}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', ESTATUS_CLASS[r.statusEmbarque])}>
                    {ESTATUS_REPORTE_LABEL[r.statusEmbarque]}
                  </span>
                </td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{fmtFecha(r.fechaShipment)}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-center text-green-700 font-medium">{r.completadas}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-center text-amber-700 font-medium">{r.pendientes}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-center text-red-700 font-medium">{r.vencidas}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-center">
                  <button
                    onClick={() => setDetalle(r)}
                    className="text-brand-600 hover:underline text-xs font-medium"
                  >
                    Detalles
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {data && data.pagination.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-ink-400">Mostrar</span>
            <Select
              value={String(f.perPage)}
              onValueChange={(v) => setF({ ...f, perPage: Number(v), page: 1 })}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPCIONES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-ink-400">de {data.pagination.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-ink-400">
              Página {f.page} de {totalPages}
            </span>
            <Button variant="secondary" size="sm" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })}>
              Anterior
            </Button>
            <Button variant="secondary" size="sm" disabled={f.page >= totalPages} onClick={() => setF({ ...f, page: f.page + 1 })}>
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {detalle && <DetalleModal row={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}

const RESULTADO_CLASS: Record<OperativoDetalleRow['resultado'], string> = {
  'A tiempo': 'text-green-700',
  Tarde: 'text-red-700 font-semibold',
  Pendiente: 'text-amber-700',
  Vencida: 'text-red-700 font-semibold',
};

/** Modal con la evidencia (auditoría) tarea por tarea de un shipment. */
function DetalleModal({
  row,
  onClose,
}: {
  row: ReporteOperativoRow;
  onClose: () => void;
}) {
  const [exp, setExp] = useState(false);
  const q = useQuery({
    queryKey: ['operativo-detalle', row.posicion],
    queryFn: () =>
      api<{ items: OperativoDetalleRow[] }>('/reports/operativos/detalle', {
        params: { bookingIdExterno: row.posicion },
      }),
  });
  const items = q.data?.items ?? [];
  async function onExport() {
    setExp(true);
    try {
      await apiDownload(
        '/reports/operativos/detalle/export',
        `Detalle ${row.posicion}.xlsx`,
        { bookingIdExterno: row.posicion },
      );
    } finally {
      setExp(false);
    }
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-[98vw] max-h-[90vh] rounded-lg bg-surface-1 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-ink-200">
          <div>
            <h3 className="text-base font-semibold">Evidencia · {row.posicion}</h3>
            <p className="text-xs text-ink-400">
              {row.operativo} · {row.cliente}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onExport} disabled={exp || !items.length}>
              <Download className="h-4 w-4 mr-1" />
              {exp ? 'Generando…' : 'Exportar'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
        <div className="overflow-auto p-1">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
                {(
                  [
                    ['Tarea', undefined],
                    ['Fase', undefined],
                    ['Alta', 'Cuándo se dio de alta la tarea'],
                    ['Completada', 'Cuándo se marcó como completada'],
                    ['Marcado por', 'Quién la marcó como completada'],
                    ['Límite', 'Fecha límite (SLA)'],
                    ['Días totales', 'Días de alta a completada (ciclo total)'],
                    ['Trabajando', 'Días de "en proceso" a completada (tiempo activo)'],
                    ['Resultado', undefined],
                    ['Comentario', 'Última nota del operativo en la tarea'],
                  ] as [string, string | undefined][]
                ).map(([h, tip]) => (
                  <th key={h} title={tip} className="px-2 py-1.5 text-left font-semibold border border-ink-100 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.isLoading && (
                <tr>
                  <td colSpan={10} className="px-2 py-6 text-center text-ink-400">
                    Cargando…
                  </td>
                </tr>
              )}
              {items.map((r, i) => (
                <tr key={i} className="hover:bg-surface-2">
                  <td className="px-2 py-1 border border-ink-100">{r.tarea}</td>
                  <td className="px-2 py-1 border border-ink-100 whitespace-nowrap text-ink-400">{r.fase ?? '—'}</td>
                  <td className="px-2 py-1 border border-ink-100 whitespace-nowrap">{fmtFecha(r.alta)}</td>
                  <td className="px-2 py-1 border border-ink-100 whitespace-nowrap">{fmtFecha(r.completada)}</td>
                  <td className="px-2 py-1 border border-ink-100 whitespace-nowrap">{r.marcadoPor ?? '—'}</td>
                  <td className="px-2 py-1 border border-ink-100 whitespace-nowrap">{fmtFecha(r.limite)}</td>
                  <td className="px-2 py-1 border border-ink-100 text-center">{r.dias ?? '—'}</td>
                  <td className="px-2 py-1 border border-ink-100 text-center">{r.tiempoTrabajando ?? '—'}</td>
                  <td className={cn('px-2 py-1 border border-ink-100 whitespace-nowrap', RESULTADO_CLASS[r.resultado])}>
                    {r.resultado}
                  </td>
                  <td
                    className="px-2 py-1 border border-ink-100 max-w-[16rem] truncate text-ink-500"
                    title={r.comentario ?? undefined}
                  >
                    {r.comentario ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
